import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { api, media } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useVisitor } from "@/lib/visitor";
import StoriesRail from "@/components/lux/StoriesRail";
import ResultsGrid from "@/components/lux/ResultsGrid";
import Filters, { filtersToQuery, readFilters } from "@/components/lux/Filters";
import SafetyCallout from "@/components/lux/SafetyCallout";
import NotFoundPage from "./NotFoundPage";

export default function AreaPage() {
  const { city, area } = useParams();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const qs = params.toString();
  const { blur } = useVisitor();
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["area", city, area, qs],
    queryFn: async () => (await api.get(`/public/cities/${city}/areas/${area}${qs ? `?${qs}` : ""}`)).data,
    placeholderData: keepPreviousData,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  });
  useSeo(data?.seo || { title: "LUX", robots: error ? "noindex,follow" : undefined });
  const apply = (f) => setParams(filtersToQuery(f), { replace: true });

  if (error?.response?.status === 404) return <NotFoundPage />;
  const a = data?.area;
  const cityName = data?.city?.name || "";
  return (
    <div>
      <section className="relative isolate overflow-hidden border-b border-lux-border">
        {a?.image && <img src={media(a.image)} alt="" className={`absolute inset-0 -z-10 h-full w-full object-cover opacity-55 ${blur ? "blur-xl" : ""}`} />}
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0B0B0C] via-black/60 to-black/40" />
        <div className="mx-auto max-w-6xl px-4 pb-12 pt-14 sm:px-6 sm:pb-16 sm:pt-24 lg:px-8">
          <nav className="text-xs text-lux-muted" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-lux-silverBright">Home</Link> <span className="mx-1.5">/</span>
            <Link to={`/${city}`} className="hover:text-lux-silverBright" data-testid="area-breadcrumb-city">{cityName}</Link> <span className="mx-1.5">/</span>
            <span className="text-lux-text">{a?.name || "..."}</span>
          </nav>
          <p className="lux-label mt-8">{cityName} neighbourhood</p>
          <h1 className="mt-3 font-serif text-5xl leading-none sm:text-7xl" data-testid="area-title">{a?.name || " "}</h1>
          {data && <p className="mt-5 flex items-center gap-2 text-sm text-lux-silver"><MapPin className="h-4 w-4" /> <span data-testid="area-active-count">{data.active_count} verified advertiser{data.active_count === 1 ? " lists" : "s list"} this area</span></p>}
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {data?.stories?.length > 0 && <div className="mt-8"><StoriesRail groups={data.stories} title={`Stories in ${a?.name}`} /></div>}

        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_300px]">
          <article className="lux-prose max-w-[68ch]" data-testid="area-intro">
            <h2 className="!mt-0">About {a?.name} and staying safe</h2>
            <p>{a?.intro}</p>
          </article>
          <aside className="lux-card h-fit p-5">
            <p className="lux-label">Other areas in {cityName}</p>
            <ul className="mt-3 space-y-1">
              {(data?.other_areas || []).map((o) => (
                <li key={o.slug}><Link to={`/${city}/${o.slug}`} className="flex items-center justify-between rounded-md px-2 py-2 text-sm text-lux-text/85 hover:bg-white/5 hover:text-lux-silverBright" data-testid={`area-other-${o.slug}`}>{o.name} <span aria-hidden="true" className="text-lux-silver">&rarr;</span></Link></li>
              ))}
            </ul>
          </aside>
        </div>

        <section className="mt-14" aria-labelledby="area-results-h">
          <h2 id="area-results-h" className="font-serif text-3xl sm:text-4xl">Independent advertisers in {a?.name}</h2>
          <p className="mt-2 text-sm text-lux-muted">Includes advertisers whose primary or nearby area is {a?.name}.</p>
          <div className="mt-6"><Filters value={filters} onApply={apply} showArea={false} total={data?.results?.length} /></div>
          <div className={`mt-6 transition-opacity ${isFetching && !isLoading ? "opacity-60" : ""}`}>
            <ResultsGrid cards={data?.results} loading={isLoading} onClear={() => apply({})} />
          </div>
        </section>
        <div className="mt-20"><SafetyCallout compact /></div>
      </div>
    </div>
  );
}
